import { UserButton } from "@clerk/react-router";
import type { ReactNode } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { usePanelId } from "../api/hooks";
import { App } from "../App";
import { LoadingSpinner } from "../components/ui/Spinner";
import { getPanelPath } from "../routing";
import { clerkAppearance } from "./clerkTheme";
import { usePortalAuthGate } from "./usePortalAuthGate";

export function ClerkAuthBoundaryCustom() {
  const {
    hasLoginError,
    isLoadingAccount,
    isMissingPanelist,
    isResettingSession,
    isSignedIn,
    resetSessionAndReturnHome,
  } = usePortalAuthGate();

  if (isMissingPanelist) {
    return <Navigate replace to="/signup/details" />;
  }

  if (isLoadingAccount) {
    return (
      <AuthShell
        title="Loading account..."
        loading
        copy="Signing you in to access your tasks, rewards, and profile."
      />
    );
  }

  if (!isSignedIn) {
    return <Navigate replace to="/sign-in" />;
  }

  if (hasLoginError) {
    return (
      <AccountLoginErrorScreen
        isSigningOut={isResettingSession}
        onTryAgain={() => void resetSessionAndReturnHome()}
      />
    );
  }

  return <App />;
}

export function AccountLoginErrorScreen({
  isSigningOut,
  onTryAgain,
}: {
  isSigningOut: boolean;
  onTryAgain: () => void;
}) {
  return (
    <AuthShell
      copy="There was an error logging into your account. Please refresh the page and try again."
      title="We couldn't log you in"
    >
      <div className="auth-actions auth-actions-stack">
        <button
          className="btn btn-primary"
          disabled={isSigningOut}
          onClick={onTryAgain}
          type="button"
        >
          {isSigningOut ? "Resetting session..." : "Try Again"}
        </button>
      </div>
    </AuthShell>
  );
}

export function AuthShell({
  children,
  copy = "Sign in to access your tasks, rewards, and profile.",
  title,
  loading,
}: {
  children?: ReactNode;
  copy?: string;
  title: string;
  loading?: boolean;
}) {
  return (
    <main className="auth-shell">
      <div className="auth-panel">
        <div className="auth-title title-md">
          {loading && <LoadingSpinner size="md" />} {title}
        </div>
        <div className="auth-copy">{copy}</div>
        {children}
      </div>
    </main>
  );
}

export function ClerkTopbarUserButton() {
  const navigate = useNavigate();
  const panelId = usePanelId();

  return (
    <div className="topbar-user-btn">
      <UserButton appearance={clerkAppearance}>
        <UserButton.MenuItems>
          <UserButton.Action
            label="My profile"
            labelIcon={<ProfileMenuIcon />}
            onClick={() => navigate(getPanelPath(panelId, "/profile"))}
          />
        </UserButton.MenuItems>
      </UserButton>
    </div>
  );
}

function ProfileMenuIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="16"
      viewBox="0 0 16 16"
      width="16"
    >
      <path
        d="M8 8.25a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.25 14a4.75 4.75 0 0 1 9.5 0"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}
